import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import axios from 'axios';
import { useContext } from 'react';
import SessionContext from '../../contexts/SessionContext';
import API_URL from '../../constants/environment';
import type { MentionableUser } from '../useGetMentionableUsers';

export interface InvoiceNote {
    _id: string;
    user: string;
    userId: string;
    note: string;
    timestamp: string;
}

const notesKey = (invoiceId: string) => ['invoice-notes', invoiceId] as const;

const getInvoiceNotes = async (sessionId: string, invoiceId: string): Promise<InvoiceNote[]> => {
    const { data } = await axios.post<{ notes: InvoiceNote[] }>(
        `${API_URL}/invoice/notes`,
        { invoiceId },
        { headers: { Authorization: sessionId } },
    );
    return data.notes;
};

export const useGetInvoiceNotes = (invoiceId: string) => {
    const sessionContext = useContext(SessionContext);
    return useQuery({
        queryKey: notesKey(invoiceId),
        queryFn: () => getInvoiceNotes(sessionContext?.sessionId ?? '', invoiceId),
        enabled: !!sessionContext?.sessionId && !!invoiceId,
        staleTime: 15 * 1000,
        refetchOnWindowFocus: false,
    });
};

const getInvoiceMentionableUsers = async (sessionId: string, invoiceId: string): Promise<MentionableUser[]> => {
    const { data } = await axios.post<MentionableUser[]>(
        `${API_URL}/invoice/mentionable-users`,
        { invoiceId },
        { headers: { Authorization: sessionId } },
    );
    return data;
};

export const useGetInvoiceMentionableUsers = (invoiceId: string) => {
    const sessionContext = useContext(SessionContext);
    return useQuery({
        queryKey: ['/invoice/mentionable-users', invoiceId],
        queryFn: () => getInvoiceMentionableUsers(sessionContext?.sessionId ?? '', invoiceId),
        enabled: !!sessionContext?.sessionId && !!invoiceId,
        staleTime: 5 * 60 * 1000,
        refetchOnWindowFocus: false,
    });
};

interface AddInvoiceNoteInput {
    invoiceId: string;
    note: string;
    mentionedUserIds?: string[];
    explicitMentionIds?: string[];
}

const addInvoiceNote = async (sessionId: string, input: AddInvoiceNoteInput) => {
    const { data } = await axios.post<{ note: InvoiceNote }>(
        `${API_URL}/invoice/notes/add`,
        input,
        { headers: { Authorization: sessionId } },
    );
    return data.note;
};

export const useAddInvoiceNote = (invoiceId: string) => {
    const sessionContext = useContext(SessionContext);
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: (input: Omit<AddInvoiceNoteInput, 'invoiceId'>) =>
            addInvoiceNote(sessionContext?.sessionId ?? '', { ...input, invoiceId }),
        onSuccess: (note) => {
            queryClient.setQueryData<InvoiceNote[]>(notesKey(invoiceId), (current) => {
                if (!current) return [note];
                if (current.some(existing => existing._id === note._id)) return current;
                return [...current, note];
            });
        },
    });
};
